/**
 * FleetMapModal — Modal com mapa Leaflet com todos os membros da empresa.
 * v2 (Phase 5):
 *   • SOS layer: heartbeat halo vermelho pulsante para membros em SOS activo
 *   • Filter dropdown: filtrar por rota/passeio activo (auto-populate por locations)
 *   • Cores determinísticas por user + sidebar
 *
 * Endpoints:
 *   GET /api/companies/{id}/members-live
 *   GET /api/companies/{id}/sos-live
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Users, Loader2, ShieldAlert } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  bg: '#0F0D0B', card: '#1A1613', ink: '#F2EBDD', inkDim: '#8A7A63',
  brand: '#E87A2A', brandDim: 'rgba(232,122,42,0.18)', sand: '#C4A275',
  sandLine: 'rgba(196,162,117,0.35)', green: '#3FA34D', red: '#DC2626',
};

const PALETTE = ['#E87A2A', '#F5C542', '#3FA34D', '#3B82F6', '#A855F7', '#EC4899', '#14B8A6', '#F97316'];
const colorFor = (userId) => {
  let h = 0;
  for (let i = 0; i < (userId || '').length; i++) h = ((h << 5) - h) + userId.charCodeAt(i);
  return PALETTE[Math.abs(h) % PALETTE.length];
};

const memberIcon = (name, heading, color, sos = false) => {
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const rot = typeof heading === 'number' ? heading : 0;
  return L.divIcon({
    html: `
      <div style="position:relative;width:44px;height:44px;">
        <div style="
          position:absolute;top:-6px;left:12px;width:20px;height:12px;
          background:${sos ? '#DC2626' : color};clip-path:polygon(50% 0%, 100% 100%, 0% 100%);
          transform:rotate(${rot}deg);transform-origin:50% 100%;
        "></div>
        <div style="
          width:42px;height:42px;border-radius:50%;
          background:${sos ? '#DC2626' : color};border:3px solid #fff;
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:900;font-size:12px;letter-spacing:.5px;
          box-shadow:0 4px 12px rgba(0,0,0,.5);
          ${sos ? 'animation:pulseSos 1.4s ease-in-out infinite;' : ''}
        ">${initials}</div>
      </div>
    `,
    className: sos ? 'fleet-map-marker fleet-map-sos' : 'fleet-map-marker',
    iconSize: [44, 44], iconAnchor: [22, 22],
  });
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  return `${Math.round(s / 3600)}h`;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) map.setView(points[0], 14, { animate: true });
    else map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
};

const FleetMapModal = ({ open, companyId, onClose }) => {
  const [locations, setLocations] = useState([]);
  const [sos, setSos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeFilter, setRouteFilter] = useState('all');

  useEffect(() => {
    if (!open || !companyId) return undefined;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [memRes, sosRes] = await Promise.all([
          axios.get(`${API}/companies/${companyId}/members-live`, { withCredentials: true }),
          axios.get(`${API}/companies/${companyId}/sos-live`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setLocations(memRes.data?.locations || []);
        setSos(sosRes.data?.sos_active || []);
      } catch { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    fetchLive();
    const t = setInterval(fetchLive, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [open, companyId]);

  const sosByUser = useMemo(() => {
    const m = {};
    for (const s of sos) if (s.user_id) m[s.user_id] = s;
    return m;
  }, [sos]);

  // Distinct active passeios (route_id + route_name)
  const passeios = useMemo(() => {
    const seen = new Map();
    for (const l of locations) {
      if (l.route_id && !seen.has(l.route_id)) {
        seen.set(l.route_id, l.route_name || l.route_id);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [locations]);

  const filtered = useMemo(() => {
    if (routeFilter === 'all') return locations;
    if (routeFilter === 'no_route') return locations.filter((l) => !l.route_id);
    return locations.filter((l) => l.route_id === routeFilter);
  }, [locations, routeFilter]);

  const points = useMemo(
    () => filtered
      .filter((m) => typeof m.lat === 'number' && typeof m.lon === 'number')
      .map((m) => [m.lat, m.lon]),
    [filtered],
  );

  if (!open) return null;

  const activeSos = filtered.filter((m) => sosByUser[m.user_id]).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
      data-testid="fleet-map-modal"
    >
      <div
        className="w-full max-w-6xl rounded-xl border overflow-hidden flex flex-col"
        style={{ backgroundColor: C.card, borderColor: C.brand, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + Filter */}
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: `1px solid ${C.sandLine}` }}>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" style={{ color: C.brand }} />
            <div>
              <div className="text-xs font-black tracking-widest uppercase" style={{ color: C.brand }}>
                MAPA DA FROTA
              </div>
              <div className="text-sm flex items-center gap-2" style={{ color: C.inkDim }}>
                {filtered.length} membro{filtered.length === 1 ? '' : 's'} · refresh 15s
                {activeSos > 0 && (
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black"
                    style={{ backgroundColor: C.red, color: '#fff', animation: 'pulseSos 1.4s ease-in-out infinite' }}
                    data-testid="fleet-map-sos-count"
                  >
                    <ShieldAlert className="w-3 h-3" /> {activeSos} SOS
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value)}
              className="text-xs px-2 py-1.5 rounded"
              style={{
                background: 'rgba(0,0,0,0.5)', color: C.ink,
                border: `1px solid ${C.sandLine}`, outline: 'none',
              }}
              data-testid="fleet-map-passeio-filter"
            >
              <option value="all">Todos ({locations.length})</option>
              {passeios.map((p) => (
                <option key={p.id} value={p.id}>
                  🏁 {p.name}
                </option>
              ))}
              <option value="no_route">Sem passeio activo</option>
            </select>
            <button
              onClick={onClose}
              className="p-2 rounded hover:bg-white/10 transition"
              data-testid="fleet-map-close"
            >
              <X className="w-5 h-5" style={{ color: C.ink }} />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden" style={{ minHeight: 400 }}>
          <div className="flex-1 relative">
            {loading && locations.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: C.brand }} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <div style={{ fontSize: 56 }}>🛰️</div>
                <div className="text-base font-bold" style={{ color: C.ink }}>Sem GPS activo</div>
                <div className="text-sm" style={{ color: C.inkDim }}>
                  {routeFilter === 'all'
                    ? 'Nenhum membro da empresa está a partilhar localização.'
                    : 'Nenhum membro neste filtro. Muda o filtro acima.'}
                </div>
              </div>
            ) : (
              <MapContainer
                center={points[0] || [39.5, -8.0]}
                zoom={12}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; OSM · Tectur 4×4'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={points} />
                {/* SOS halos — desenhados PRIMEIRO para ficarem por baixo dos avatares */}
                {filtered
                  .filter((m) => sosByUser[m.user_id] && typeof m.lat === 'number' && typeof m.lon === 'number')
                  .map((m) => (
                    <React.Fragment key={`sos-${m.user_id}`}>
                      <CircleMarker
                        center={[m.lat, m.lon]}
                        radius={42}
                        pathOptions={{ color: '#DC2626', weight: 2, opacity: 0.55, fillOpacity: 0.15, fillColor: '#DC2626' }}
                      />
                      <CircleMarker
                        center={[m.lat, m.lon]}
                        radius={22}
                        pathOptions={{ color: '#DC2626', weight: 0, fillOpacity: 0.35, fillColor: '#DC2626' }}
                      />
                    </React.Fragment>
                  ))}
                {/* Member markers */}
                {filtered
                  .filter((m) => typeof m.lat === 'number' && typeof m.lon === 'number')
                  .map((m) => {
                    const color = colorFor(m.user_id);
                    const kmh = m.speed != null ? Math.round(m.speed * 3.6) : null;
                    const inSos = !!sosByUser[m.user_id];
                    return (
                      <Marker
                        key={m.user_id}
                        position={[m.lat, m.lon]}
                        icon={memberIcon(m.name, m.heading, color, inSos)}
                      >
                        <Popup>
                          <div className="text-sm min-w-[200px]">
                            <div className="font-bold text-slate-800 flex items-center gap-2">
                              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: inSos ? '#DC2626' : color }} />
                              {m.name}
                              {inSos && <span className="text-red-700 font-black text-xs">🚨 SOS</span>}
                            </div>
                            {m.route_name && (
                              <div className="text-xs text-slate-600 mt-1">🏁 {m.route_name}</div>
                            )}
                            <div className="text-xs text-slate-600 mt-2">
                              {kmh != null && <>🚗 <b>{kmh} km/h</b> · </>}
                              atualizado há {fmtAgo(m.updated_at)}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1 font-mono">
                              {m.lat.toFixed(5)}, {m.lon.toFixed(5)}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
              </MapContainer>
            )}
          </div>

          {filtered.length > 0 && (
            <div
              className="w-64 overflow-y-auto border-l"
              style={{ backgroundColor: C.bg, borderColor: C.sandLine }}
            >
              <div className="px-3 py-2 text-[10px] font-black tracking-widest sticky top-0 z-10"
                style={{ color: C.inkDim, backgroundColor: C.bg, borderBottom: `1px solid ${C.sandLine}` }}>
                ACTIVOS ({filtered.length})
              </div>
              <div className="p-2 space-y-1.5">
                {filtered.map((m) => {
                  const color = colorFor(m.user_id);
                  const ageMin = Math.max(0, Math.round((Date.now() - new Date(m.updated_at).getTime()) / 60000));
                  const isFresh = ageMin < 5;
                  const kmh = m.speed != null ? Math.round(m.speed * 3.6) : null;
                  const inSos = !!sosByUser[m.user_id];
                  return (
                    <div
                      key={m.user_id}
                      className="flex items-center gap-2 p-2 rounded"
                      style={{
                        backgroundColor: inSos ? 'rgba(220,38,38,0.16)' : 'rgba(0,0,0,0.35)',
                        borderLeft: `3px solid ${inSos ? C.red : color}`,
                      }}
                      data-testid={`fleet-map-member-${m.user_id}`}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                        style={{
                          backgroundColor: inSos ? C.red : color, color: '#fff',
                          animation: inSos ? 'pulseSos 1.4s ease-in-out infinite' : 'none',
                        }}
                      >
                        {(m.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate" style={{ color: C.ink }}>
                          {inSos && '🚨 '}{m.name}
                        </div>
                        <div className="text-[10px]" style={{ color: isFresh ? C.green : C.inkDim }}>
                          {kmh != null ? `${kmh} km/h · ` : ''}
                          {ageMin === 0 ? 'agora' : `há ${ageMin}min`}
                        </div>
                        {m.route_name && (
                          <div className="text-[10px] truncate" style={{ color: C.sand }}>
                            🏁 {m.route_name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetMapModal;
